import { NavItemProps, NavItemWithChildrenProps } from '@percent/lemonade'
import {
  POSTHOG_FEATURE_FLAGS,
  PostHogFeatureFlag
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import {
  NavConfigItem,
  NavConfigChildItem,
  NavItemPermissionConfig,
  CreateNavItemParams,
  GetADNavItemsParams,
  UserHasPermissions,
  BadgeCounts
} from './getADNavItems.types'

export function resolveNavAccess({
  permissionEnabledFlag,
  legacyPermissionFlag,
  permissions,
  activeFeatureFlags,
  userHasPermissions
}: NavItemPermissionConfig & {
  activeFeatureFlags: PostHogFeatureFlag[]
  userHasPermissions: UserHasPermissions
}): boolean {
  if (!permissionEnabledFlag && !legacyPermissionFlag && !permissions) {
    return true
  }

  if (permissionEnabledFlag && activeFeatureFlags.includes(permissionEnabledFlag)) {
    return permissions ? userHasPermissions(permissions) : true
  }

  if (legacyPermissionFlag) {
    return activeFeatureFlags.includes(legacyPermissionFlag)
  }

  // The domain's FGA rollout flag is off, so the backend skips its permission check and
  // any admin may still use the page. With no legacy flag to fall back on there is
  // nothing left to gate on, so fail open exactly as AccessRoute does. Items that pass a
  // permissionEnabledFlag with no permissions are using it as a plain feature flag, so
  // they keep falling through to the flag check below.
  if (permissionEnabledFlag && permissions) {
    return true
  }

  if (permissions) {
    return userHasPermissions(permissions)
  }

  return false
}

function createNavItem({
  condition,
  icon,
  label,
  href,
  badge,
  children,
  pathname,
  activeFeatureFlags,
  userHasPermissions
}: CreateNavItemParams): NavItemProps | NavItemWithChildrenProps | null {
  if (!condition) return null

  if (children && children.length > 0) {
    const visibleChildren = children.filter(child =>
      resolveNavAccess({
        permissionEnabledFlag: child.permissionEnabledFlag,
        legacyPermissionFlag: child.legacyPermissionFlag,
        permissions: child.permissions,
        activeFeatureFlags,
        userHasPermissions
      })
    )

    if (visibleChildren.length === 0) return null

    const mappedChildren = visibleChildren.map(child => {
      const hrefPath = child.href.split('?')[0]

      return {
        label: child.label,
        href: child.href,
        isActive: pathname === hrefPath || pathname.startsWith(`${hrefPath}/`),
        ...(child.badge && { badge: child.badge })
      }
    })

    const isAnyChildActive = mappedChildren.some(child => child.isActive)

    return {
      icon,
      label,
      href: visibleChildren[0]?.href || href,
      isActive: isAnyChildActive,
      ...(badge && { badge }),
      children: mappedChildren
    } as NavItemWithChildrenProps
  }

  const baseHref = href.includes('?') ? href.split(/\?/)[0] : href.split(/\/+/).find(Boolean) ?? ''

  return {
    icon,
    label,
    href,
    isActive: pathname.includes(baseHref),
    ...(badge && { badge })
  }
}

// An admin who cannot list accounts reaches the accounts page only for its Services tab, so links straight
// there; last, so the Accounts group still opens where it did.
function getAccountsNavItems(t: GetADNavItemsParams['t'], servicesOnly: boolean): NavConfigChildItem[] {
  return [
    {
      label: t('menu.goodstack'),
      href: '/accounts',
      permissions: 'accounts:can_list'
    },
    {
      label: t('typography.organizations'),
      href: '/organizations',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_ORGANISATIONS,
      permissions: 'organisations:can_read'
    },
    { label: t('menu.partners'), href: '/partners' },
    ...(servicesOnly ? [{ label: t('menu.services'), href: '/accounts/services' }] : [])
  ]
}

function getDataNavItems(t: GetADNavItemsParams['t']): NavConfigChildItem[] {
  return [
    {
      label: t('menu.claims'),
      href: '/claims?accepted=false&rejected=false'
    },
    {
      label: t('menu.registries'),
      href: '/registries',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_REGISTRIES,
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_REGISTRIES,
      permissions: 'registries:can_read'
    },
    {
      label: t('menu.oracle'),
      href: '/ralph/oracle',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_RALPH_ORACLE,
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_RALPH_ORACLE,
      permissions: 'ralph_oracle:can_read'
    }
  ]
}

function getFinanceNavItems(t: GetADNavItemsParams['t'], badgeCounts?: BadgeCounts): NavConfigChildItem[] {
  return [
    {
      label: t('menu.donationRequests'),
      href: '/donation-requests',
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_DONATION_REQUESTS
    },
    {
      label: t('menu.disbursements'),
      href: '/disbursements',
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_DISBURSEMENTS
    },
    {
      label: t('menu.donationMatchRequests'),
      href: '/donation-match-requests?status=processing',
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_DONATION_MATCH_REQUESTS
    },
    {
      label: t('menu.bankDetails'),
      href: '/bank-details-reviews',
      badge: formatBadgeCount(badgeCounts?.bankDetails),
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_BANK_DETAILS_READ
    },
    {
      label: t('menu.donations'),
      href: '/donations',
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_DONATIONS_TABLE
    },
    {
      label: t('menu.sewaOnboarding'),
      href: '/sewa-onboarding',
      permissions: 'donations:can_read_sewa'
    }
  ]
}

function getGrantsNavItems(t: GetADNavItemsParams['t']): NavConfigChildItem[] {
  return [
    {
      label: t('menu.programs'),
      href: '/grants-programs',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_GRANTS,
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_GRANT_PROGRAMS,
      permissions: 'grants:can_read_program'
    },
    {
      label: t('menu.forms'),
      href: '/grants-forms',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_GRANTS,
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_GRANT_PROGRAMS,
      permissions: 'grants:can_read_form'
    },
    {
      label: t('menu.assignmentRules'),
      href: '/grants-assignment-rules',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_GRANTS,
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_GRANT_PROGRAMS,
      permissions: 'grants:can_read_program'
    }
  ]
}

function getVerificationsNavItems(t: GetADNavItemsParams['t']): NavConfigChildItem[] {
  return [
    {
      label: t('menu.verifications'),
      href: '/validation-submissions?statuses=pending&sort=id&direction=ASC',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VALIDATION_SUBMISSIONS,
      permissions: 'validation_submissions:can_read'
    },
    {
      label: t('menu.validations'),
      href: '/validations?status=pending&sort=id&direction=ASC',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VALIDATION_REQUESTS,
      permissions: 'validation_requests:can_read'
    },
    {
      label: t('menu.eligibility'),
      href: '/eligibility?status=pending&sort=id&direction=ASC',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_ELIGIBILITY_SUBSCRIPTIONS,
      permissions: 'eligibility_subscriptions:can_read'
    },
    {
      label: t('menu.compliance'),
      href: '/compliance?status=pending&sort=id&direction=ASC'
    },
    {
      label: t('menu.agentVerification'),
      href: '/agent-verification'
    },
    {
      label: t('menu.activity'),
      href: '/activity?status=pending&sort=createdAt&direction=ASC',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_ACTIVITY,
      permissions: 'activity:can_read'
    },
    {
      label: t('menu.monetaryVerifications'),
      href: '/monetary-verifications'
    },
    {
      label: t('menu.autoRejectionList'),
      href: '/auto-rejection-list',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_AGENT_VERIFICATIONS_AUTO_REJECTION_LIST,
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_AUTO_REJECTION_LIST,
      permissions: 'agent_verifications_auto_rejection_list:can_read'
    },
    {
      label: t('menu.autoApprovalList'),
      href: '/allow-list',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VERIFICATIONS_ALLOW_LIST,
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_AUTO_ALLOW_LIST,
      permissions: 'verifications_allow_list:can_read'
    },
    {
      label: t('menu.autoRequestRejectionList'),
      href: '/validation-request-block-list',
      permissionEnabledFlag: POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VALIDATION_REQUEST_BLOCKLIST,
      legacyPermissionFlag: POSTHOG_FEATURE_FLAGS.AD_VR_BLOCK_LIST,
      permissions: 'validation_request_blocklist:can_read'
    }
  ]
}

function formatBadgeCount(count?: number) {
  if (!count || count <= 0) return undefined

  return { text: count > 99 ? '99+' : String(count), variant: 'default' as const }
}

export function getADNavItems({
  pathname,
  activeFeatureFlags,
  t,
  badgeCounts,
  userHasPermissions
}: GetADNavItemsParams): (NavItemProps | NavItemWithChildrenProps)[] {
  const servicesOnly =
    activeFeatureFlags.includes(POSTHOG_FEATURE_FLAGS.AD_SERVICES) && !userHasPermissions('accounts:can_list')
  const accountsChildren = getAccountsNavItems(t, servicesOnly)
  const dataChildren = getDataNavItems(t)
  const financeChildren = getFinanceNavItems(t, badgeCounts)
  const grantsChildren = getGrantsNavItems(t)
  const verificationsChildren = getVerificationsNavItems(t)

  const navConfig: NavConfigItem[] = [
    {
      icon: 'menu-profile',
      label: t('menu.accounts'),
      href: '/organizations',
      condition: true,
      children: accountsChildren
    },
    {
      icon: 'menu-dashboard',
      label: t('menu.data'),
      href: '/claims',
      condition: true,
      children: dataChildren
    },
    {
      icon: 'menu-donations',
      label: t('menu.finance'),
      href: '/donation-requests',
      condition: true,
      children: financeChildren
    },
    {
      icon: 'menu-grants',
      label: t('menu.grantsNav'),
      href: '/grants-programs',
      condition: activeFeatureFlags.includes(POSTHOG_FEATURE_FLAGS.AD_GRANT_PROGRAMS),
      children: grantsChildren
    },
    {
      icon: 'menu-verifications',
      label: t('menu.verifications'),
      href: '/validations',
      condition: true,
      children: verificationsChildren
    }
  ]

  const items = navConfig.map(item =>
    createNavItem({
      ...item,
      pathname,
      activeFeatureFlags,
      userHasPermissions,
      children: item.children ? item.children.map(child => ({ ...child, pathname })) : undefined
    })
  )

  return items.filter((item): item is NavItemProps | NavItemWithChildrenProps => item !== null)
}
